"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    Label,
} from "@headlessui/react";
import { Icon } from "@plura/client/components";
import Tooltip from "../../atoms/Tooltip";

export type MultiSelectOption = {
    id: number;
    name: string;
    description?: string;
    icon?: React.ReactNode;
};

type MultiSelectProps = {
    className?: string;
    classButton?: string;
    classLabel?: string;
    label?: string;
    innerLabel?: string;
    tooltip?: string;
    value: MultiSelectOption[];
    onChange: (value: MultiSelectOption[]) => void;
    options: MultiSelectOption[];
    isBlack?: boolean;
    placeholder?: string;
    /** Custom button label, e.g. "All forms" when value is empty. */
    emptyLabel?: string;
    searchable?: boolean;
    searchPlaceholder?: string;
    emptyMessage?: string;
    disabled?: boolean;
    loading?: boolean;
};

const MultiSelect = ({
    className,
    classButton,
    classLabel,
    label,
    innerLabel,
    tooltip,
    value,
    onChange,
    options,
    isBlack,
    placeholder,
    emptyLabel,
    searchable = false,
    searchPlaceholder = "Search…",
    emptyMessage = "No results found",
    disabled = false,
    loading = false,
}: MultiSelectProps) => {
    const [flipped, setFlipped] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const buttonRef = useRef<HTMLButtonElement>(null);
    const optionsElRef = useRef<HTMLElement | null>(null);

    const detectPosition = useCallback(() => {
        if (!optionsElRef.current || !buttonRef.current) return;
        const btnRect = buttonRef.current.getBoundingClientRect();
        const optRect = optionsElRef.current.getBoundingClientRect();
        setFlipped(optRect.bottom <= btnRect.top + 4);
    }, []);

    const optionsCallbackRef = useCallback(
        (el: HTMLElement | null) => {
            optionsElRef.current = el;
            if (el) requestAnimationFrame(detectPosition);
        },
        [detectPosition]
    );

    useEffect(() => {
        if (!optionsElRef.current) return;
        const onReposition = () => requestAnimationFrame(detectPosition);
        window.addEventListener("scroll", onReposition, true);
        window.addEventListener("resize", onReposition);
        return () => {
            window.removeEventListener("scroll", onReposition, true);
            window.removeEventListener("resize", onReposition);
        };
    }, [detectPosition]);

    const filteredOptions = useMemo(() => {
        if (!searchable || !searchQuery.trim()) return options;
        const q = searchQuery.toLowerCase().trim();
        return options.filter(
            (option) =>
                option.name.toLowerCase().includes(q) ||
                option.description?.toLowerCase().includes(q)
        );
    }, [options, searchQuery, searchable]);

    const openBelow = !flipped;
    const buttonBgClass = classButton || "bg-b-surface2";

    const emptyTriggerLabel = emptyLabel ?? placeholder ?? "Select…";

    if (loading) {
        return (
            <div className={`${innerLabel ? "pt-1.5" : ""} ${className || ""}`}>
                {label && !innerLabel && (
                    <div className="flex items-center mb-4">
                        <div className="text-button">{label}</div>
                        {tooltip && (
                            <Tooltip className="ml-1.5" content={tooltip} />
                        )}
                    </div>
                )}
                <div className="relative" aria-busy="true" aria-label="Loading options">
                    {innerLabel && (
                        <div className={`absolute -top-2.5 left-6 px-1 text-caption pointer-events-none z-1 ${classLabel || "bg-b-surface2"}`}>
                            {innerLabel}
                        </div>
                    )}
                    <div
                        className={`flex h-12 w-full items-center justify-between rounded-3xl border border-s-stroke2 ${innerLabel ? "pl-6.5" : "pl-4.5"} pr-3 ${buttonBgClass}`}
                    >
                        <div className="h-3.5 w-[42%] max-w-48 animate-pulse rounded-full bg-s-stroke2" />
                        <div className="size-4 shrink-0 animate-pulse rounded-full bg-s-stroke2/80" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Listbox
            className={`${innerLabel ? "pt-1.5" : ""} ${className || ""}`}
            value={value}
            onChange={(next) => {
                setSearchQuery("");
                onChange(next);
            }}
            disabled={disabled}
            multiple
            as="div"
            by="id"
        >
            {({ open }) => (
                <>
                    {label && !innerLabel && (
                        <Label className="flex items-center mb-4">
                            <div className="text-button">{label}</div>
                            {tooltip && (
                                <Tooltip className="ml-1.5" content={tooltip} />
                            )}
                        </Label>
                    )}
                    <div className="relative">
                        {innerLabel && (
                            <div className={`absolute -top-2.5 left-6 px-1 text-caption pointer-events-none z-1 ${classLabel || "bg-b-surface2"}`}>
                                {innerLabel}
                            </div>
                        )}
                        <ListboxButton
                            ref={buttonRef}
                            disabled={disabled}
                            className={`group flex justify-between items-center gap-2 w-full min-h-12 pr-3 py-1.5 border border-s-stroke2 rounded-3xl text-body-2 text-t-primary fill-t-secondary outline-none transition-all disabled:cursor-not-allowed disabled:opacity-50 ${!open && !disabled ? "data-[hover]:border-s-highlight data-[hover]:text-t-primary" : ""} ${innerLabel ? "pl-6.5" : "pl-2"} ${isBlack
                                ? "bg-linear-to-b border-transparent from-[#2C2C2C] to-[#282828] !text-t-light !fill-t-light dark:from-shade-10 dark:to-[#DEDEDE]"
                                : ""
                                } ${open && openBelow
                                    ? "!rounded-b-none !border-s-subtle !border-b-transparent text-t-primary"
                                    : ""
                                } ${open && !openBelow
                                    ? "!rounded-t-none !border-s-subtle !border-t-transparent text-t-primary"
                                    : ""
                                } ${buttonBgClass}`}
                        >
                            {value.length === 0 ? (
                                <span className={`truncate ${innerLabel ? "" : "pl-2.5"} text-t-secondary/50`}>
                                    {emptyTriggerLabel}
                                </span>
                            ) : (
                                <span className="flex flex-wrap items-center gap-1.5 min-w-0">
                                    {value.map((item) => (
                                        <span
                                            key={item.id}
                                            className="inline-flex items-center gap-1 max-w-full pl-2.5 pr-1 py-0.5 rounded-full bg-s-stroke2 text-caption text-t-primary"
                                        >
                                            <span className="truncate">{item.name}</span>
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                aria-label={`Remove ${item.name}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onChange(value.filter((v) => v.id !== item.id));
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onChange(value.filter((v) => v.id !== item.id));
                                                    }
                                                }}
                                                className="shrink-0 inline-flex items-center justify-center size-4 rounded-full hover:bg-s-stroke2/60 cursor-pointer"
                                            >
                                                <Icon className="!size-2.5 fill-t-secondary" name="close" />
                                            </span>
                                        </span>
                                    ))}
                                </span>
                            )}
                            <Icon
                                className={`shrink-0 ml-auto fill-inherit transition-transform ${open && openBelow ? "rotate-180" : ""}`}
                                name="chevron"
                            />
                        </ListboxButton>
                        <ListboxOptions
                            ref={optionsCallbackRef}
                            className={`z-100 [--anchor-gap:-2px] w-[var(--button-width)] bg-b-surface2 border border-s-subtle origin-top transition duration-200 ease-out outline-none data-[closed]:scale-95 data-[closed]:opacity-0 ${openBelow
                                ? "shadow-depth border-t-0 rounded-b-[1.25rem]"
                                : "shadow-depth-flipped border-b-0 rounded-t-[1.25rem]"
                                } ${isBlack ? "!border-[#2C2C2C]" : ""
                                } ${searchable ? "px-0 pb-0" : "px-2.25 pb-2.25"}`}
                            anchor="bottom"
                            transition
                        >
                            {searchable && (
                                <div
                                    className={`sticky top-0 z-1 border-b border-s-subtle bg-b-surface2 px-2.25 py-2 ${openBelow ? "rounded-t-none" : "rounded-t-[1.25rem]"}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="relative">
                                        <Icon
                                            className="pointer-events-none absolute left-3 top-1/2 !size-4 -translate-y-1/2 fill-t-tertiary"
                                            name="search"
                                        />
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            placeholder={searchPlaceholder}
                                            className="h-10 w-full rounded-full border border-s-stroke2 bg-b-surface2 pl-9 pr-3 text-caption text-t-primary outline-none transition-colors placeholder:text-t-tertiary hover:border-s-highlight focus:border-s-highlight"
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className={`max-h-72 overflow-y-auto ${searchable ? "px-2.25 pb-2.25 pt-1" : ""} [scrollbar-width:thin]`}>
                                {filteredOptions.length === 0 ? (
                                    <div className="px-2.25 py-3 text-center text-caption text-t-tertiary">
                                        {emptyMessage}
                                    </div>
                                ) : (
                                    filteredOptions.map((option) => (
                                        <ListboxOption
                                            className="group/opt relative flex items-center gap-3 pl-2.25 py-2 pr-3 rounded-lg text-body-2 text-t-secondary cursor-pointer transition-colors data-[focus]:text-t-primary data-[selected]:text-t-primary"
                                            key={option.id}
                                            value={option}
                                        >
                                            <span
                                                aria-hidden
                                                className="relative shrink-0 size-5 rounded-[5px] border-2 border-s-stroke2 transition-colors before:absolute before:top-[3px] before:left-[3px] before:w-[10px] before:h-[10px] before:rounded-[2px] before:bg-primary-01 before:opacity-0 before:transition-opacity group-data-[selected]/opt:border-primary-01/30 group-data-[selected]/opt:before:opacity-100"
                                            />
                                            {option.icon && <div className="shrink-0 flex items-center">{option.icon}</div>}
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate">{option.name}</div>
                                                {option.description && (
                                                    <div className="truncate text-caption text-t-tertiary">
                                                        {option.description}
                                                    </div>
                                                )}
                                            </div>
                                        </ListboxOption>
                                    ))
                                )}
                            </div>
                        </ListboxOptions>
                    </div>
                </>
            )}
        </Listbox>
    );
};

export default MultiSelect;
